// ── Service de Webhooks de saída (núcleo/aplicação) ───────────────
// Duas responsabilidades:
//   1. `notify` — disparo fire-and-forget usado pelos fluxos comerciais.
//      NUNCA rejeita: um webhook fora do ar não pode derrubar uma venda.
//   2. CRUD da configuração por workspace (Super Admin).
//
// Não conhece Supabase nem fetch — tudo passa pelas portas.

import type { IWorkspaceWebhookGateway } from '../ports/IWorkspaceWebhookGateway';
import type { IWorkspaceWebhookRepository, ListDeliveriesParams } from '../ports/IWorkspaceWebhookRepository';
import type {
  DispatchWebhookParams,
  WebhookDelivery,
  WorkspaceWebhookConfig,
  WorkspaceWebhookConfigInput,
} from '../webhookModel';
import { WORKSPACE_WEBHOOK_EVENTS } from '../webhookModel';

export interface WorkspaceWebhookDeps {
  gateway: IWorkspaceWebhookGateway;
  repo: IWorkspaceWebhookRepository;
}

export class WorkspaceWebhookService {
  constructor(private readonly deps: WorkspaceWebhookDeps) {}

  /**
   * Dispara um evento para o webhook do workspace. Non-fatal por contrato:
   * quem chama não precisa (nem deve) dar await esperando sucesso.
   */
  notify = async (params: DispatchWebhookParams): Promise<void> => {
    try {
      if (!params?.event || !params.workspaceId || !params.leadId) return;
      const result = await this.deps.gateway.dispatch(params);
      if (result.status === 'failed') {
        console.warn(`[webhook] "${params.event}" não entregue:`, result.error || 'erro desconhecido');
      }
    } catch (err) {
      console.warn(`[webhook] falha ao disparar "${params?.event}":`, (err as Error)?.message || err);
    }
  };

  getConfig = (workspaceId: string): Promise<WorkspaceWebhookConfig | null> =>
    this.deps.repo.getConfig(workspaceId);

  listConfigs = (): Promise<WorkspaceWebhookConfig[]> => this.deps.repo.listConfigs();

  saveConfig = async (input: WorkspaceWebhookConfigInput): Promise<void> => {
    const url = (input.url || '').trim();
    if (input.enabled) {
      if (!url) throw new Error('Informe a URL do webhook para ativá-lo.');
      if (!/^https?:\/\//i.test(url)) throw new Error('A URL precisa começar com http:// ou https://.');
      if (!(input.secret || '').trim()) {
        throw new Error('Informe o segredo — ele assina o payload (HMAC-SHA256) e prova a origem.');
      }
      if (!WORKSPACE_WEBHOOK_EVENTS.some(event => input.events?.[event])) {
        throw new Error('Selecione ao menos um evento.');
      }
    }
    await this.deps.repo.saveConfig({ ...input, url });
  };

  deleteConfig = (workspaceId: string): Promise<void> => this.deps.repo.deleteConfig(workspaceId);

  listDeliveries = (params: ListDeliveriesParams = {}): Promise<WebhookDelivery[]> =>
    this.deps.repo.listDeliveries(params);
}
